import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import type React from 'react';

type CheckboxGroupProps = {
  className?: string;
} & React.ComponentPropsWithoutRef<'div'>;

export function CheckboxGroup({ className, ...props }: CheckboxGroupProps) {
  return (
    <div
      data-slot="control"
      {...props}
      className={clsx(
        className,
        // Basic groups
        'tw:space-y-3',
        // With descriptions
        'tw:has-[[data-slot=description]]:space-y-6 tw:[&_[data-slot=label]]:has-[[data-slot=description]]:font-medium'
      )}
    />
  );
}

export function CheckboxField({
  className,
  ...props
}: { className?: string } & Omit<Headless.FieldProps, 'as' | 'className'>) {
  return (
    <Headless.Field
      data-slot="field"
      {...props}
      className={clsx(
        className,
        // Base layout
        'tw:grid tw:grid-cols-[1.125rem_1fr] tw:items-center tw:gap-x-4 tw:gap-y-1 tw:sm:grid-cols-[1rem_1fr]',
        // Control layout
        'tw:[&>[data-slot=control]]:col-start-1 tw:[&>[data-slot=control]]:row-start-1 tw:[&>[data-slot=control]]:justify-self-center',
        // Label layout
        'tw:[&>[data-slot=label]]:col-start-2 tw:[&>[data-slot=label]]:row-start-1 tw:[&>[data-slot=label]]:justify-self-start',
        // Description layout
        'tw:[&>[data-slot=description]]:col-start-2 tw:[&>[data-slot=description]]:row-start-2',
        // With description
        'tw:[&_[data-slot=label]]:has-[[data-slot=description]]:font-medium'
      )}
    />
  );
}

const base = [
  // Basic layout
  'tw:relative tw:isolate tw:flex tw:size-[1.125rem] tw:items-center tw:justify-center tw:rounded-[0.3125rem] tw:sm:size-4',
  // Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
  'tw:before:absolute tw:before:inset-0 tw:before:-z-10 tw:before:rounded-[calc(0.3125rem-1px)] tw:before:shadow',
  // Background color when checked
  'tw:before:group-data-[checked]:bg-[var(--checkbox-checked-bg)]',
  // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
  'tw:dark:before:hidden',
  // Background color applied to control in dark mode
  'tw:dark:bg-white/5 tw:dark:group-data-[checked]:bg-[--checkbox-checked-bg]',
  // Border
  'tw:border tw:border-zinc-950/15 tw:group-data-[checked]:border-transparent tw:group-data-[checked]:group-data-[hover]:border-transparent tw:group-data-[hover]:border-zinc-950/30 tw:group-data-[checked]:bg-[var(--checkbox-checked-border)]',
  'tw:dark:border-white/15 tw:dark:group-data-[checked]:border-white/5 tw:dark:group-data-[checked]:group-data-[hover]:border-white/5 tw:dark:group-data-[hover]:border-white/30',
  // Inner highlight shadow
  'tw:after:absolute tw:after:inset-0 tw:after:rounded-[calc(0.3125rem-1px)] tw:after:shadow-[inset_0_1px_theme(colors.white/15%)]',
  'tw:dark:after:-inset-px tw:dark:after:hidden tw:dark:after:rounded-[0.3125rem] tw:dark:group-data-[checked]:after:block',
  // Focus ring
  'tw:group-data-[focus]:outline tw:group-data-[focus]:outline-2 tw:group-data-[focus]:outline-offset-2 tw:group-data-[focus]:outline-blue-500',
  // Disabled state
  'tw:group-data-[disabled]:opacity-50',
  'tw:group-data-[disabled]:border-zinc-950/25 tw:group-data-[disabled]:bg-zinc-950/5 tw:group-data-[disabled]:[--checkbox-check:theme(colors.zinc.950/70%)] tw:group-data-[disabled]:before:bg-transparent',
  'tw:dark:group-data-[disabled]:border-white/40 tw:dark:group-data-[disabled]:bg-white/[5%] tw:dark:group-data-[disabled]:[--checkbox-check:theme(colors.white/70%)] tw:dark:group-data-[disabled]:group-data-[checked]:after:hidden',
  // Forced colors mode
  'tw:forced-colors:[--checkbox-check:HighlightText] tw:forced-colors:[--checkbox-checked-bg:Highlight] tw:forced-colors:group-data-[disabled]:[--checkbox-check:Highlight]',
  'tw:dark:forced-colors:[--checkbox-check:HighlightText] tw:dark:forced-colors:[--checkbox-checked-bg:Highlight] tw:dark:forced-colors:group-data-[disabled]:[--checkbox-check:Highlight]',
];

const colors = {
  'dark/zinc': [
    'tw:[--checkbox-check:theme(colors.white)] tw:[--checkbox-checked-bg:theme(colors.zinc.900)] tw:[--checkbox-checked-border:theme(colors.zinc.950/90%)]',
    'tw:dark:[--checkbox-checked-bg:theme(colors.zinc.600)]',
  ],
  'dark/white': [
    'tw:[--checkbox-check:theme(colors.white)] tw:[--checkbox-checked-bg:theme(colors.zinc.900)] tw:[--checkbox-checked-border:theme(colors.zinc.950/90%)]',
    'tw:dark:[--checkbox-check:theme(colors.zinc.900)] tw:dark:[--checkbox-checked-bg:theme(colors.white)] tw:dark:[--checkbox-checked-border:theme(colors.zinc.950/15%)]',
  ],
  white:
    'tw:[--checkbox-check:theme(colors.zinc.900)] tw:[--checkbox-checked-bg:theme(colors.white)] tw:[--checkbox-checked-border:theme(colors.zinc.950/15%)]',
  dark: 'tw:[--checkbox-check:theme(colors.white)] tw:[--checkbox-checked-bg:theme(colors.zinc.900)] tw:[--checkbox-checked-border:theme(colors.zinc.950/90%)]',
  zinc: 'tw:[--checkbox-check:theme(colors.white)] tw:[--checkbox-checked-bg:theme(colors.zinc.600)] tw:[--checkbox-checked-border:theme(colors.zinc.700/90%)]',
  red: 'tw:[--checkbox-check:theme(colors.white)] tw:[--checkbox-checked-bg:theme(colors.red.600)] tw:[--checkbox-checked-border:theme(colors.red.700/90%)]',
  orange:
    'tw:[--checkbox-check:theme(colors.white)] tw:[--checkbox-checked-bg:theme(colors.orange.500)] tw:[--checkbox-checked-border:theme(colors.orange.600/90%)]',
  amber:
    'tw:[--checkbox-check:theme(colors.amber.950)] tw:[--checkbox-checked-bg:theme(colors.amber.400)] tw:[--checkbox-checked-border:theme(colors.amber.500/80%)]',
  yellow:
    'tw:[--checkbox-check:theme(colors.yellow.950)] tw:[--checkbox-checked-bg:theme(colors.yellow.300)] tw:[--checkbox-checked-border:theme(colors.yellow.400/80%)]',
  lime: 'tw:[--checkbox-check:theme(colors.lime.950)] tw:[--checkbox-checked-bg:theme(colors.lime.300)] tw:[--checkbox-checked-border:theme(colors.lime.400/80%)]',
  green:
    'tw:[--checkbox-check:theme(colors.white)] tw:[--checkbox-checked-bg:theme(colors.green.600)] tw:[--checkbox-checked-border:theme(colors.green.700/90%)]',
  emerald:
    'tw:[--checkbox-check:theme(colors.white)] tw:[--checkbox-checked-bg:theme(colors.emerald.600)] tw:[--checkbox-checked-border:theme(colors.emerald.700/90%)]',
  teal: 'tw:[--checkbox-check:theme(colors.white)] tw:[--checkbox-checked-bg:theme(colors.teal.600)] tw:[--checkbox-checked-border:theme(colors.teal.700/90%)]',
  cyan: 'tw:[--checkbox-check:theme(colors.cyan.950)] tw:[--checkbox-checked-bg:theme(colors.cyan.300)] tw:[--checkbox-checked-border:theme(colors.cyan.400/80%)]',
  sky: 'tw:[--checkbox-check:theme(colors.white)] tw:[--checkbox-checked-bg:theme(colors.sky.500)] tw:[--checkbox-checked-border:theme(colors.sky.600/80%)]',
  blue: 'tw:[--checkbox-check:theme(colors.white)] tw:[--checkbox-checked-bg:theme(colors.blue.600)] tw:[--checkbox-checked-border:theme(colors.blue.700/90%)]',
  indigo:
    'tw:[--checkbox-check:theme(colors.white)] tw:[--checkbox-checked-bg:theme(colors.indigo.500)] tw:[--checkbox-checked-border:theme(colors.indigo.600/90%)]',
  violet:
    'tw:[--checkbox-check:theme(colors.white)] tw:[--checkbox-checked-bg:theme(colors.violet.500)] tw:[--checkbox-checked-border:theme(colors.violet.600/90%)]',
  purple:
    'tw:[--checkbox-check:theme(colors.white)] tw:[--checkbox-checked-bg:theme(colors.purple.500)] tw:[--checkbox-checked-border:theme(colors.purple.600/90%)]',
  fuchsia:
    'tw:[--checkbox-check:theme(colors.white)] tw:[--checkbox-checked-bg:theme(colors.fuchsia.500)] tw:[--checkbox-checked-border:theme(colors.fuchsia.600/90%)]',
  pink: 'tw:[--checkbox-check:theme(colors.white)] tw:[--checkbox-checked-bg:theme(colors.pink.500)] tw:[--checkbox-checked-border:theme(colors.pink.600/90%)]',
  rose: 'tw:[--checkbox-check:theme(colors.white)] tw:[--checkbox-checked-bg:theme(colors.rose.500)] tw:[--checkbox-checked-border:theme(colors.rose.600/90%)]',
  brandgreen1:
    'tw:[--checkbox-check:theme(colors.white)] tw:[--checkbox-checked-bg:theme(colors.brand-green-1)] tw:[--checkbox-checked-border:theme(colors.brand-green-1/90%)]',
  brandgreen:
    'tw:[--checkbox-check:theme(colors.white)] tw:[--checkbox-checked-bg:theme(colors.brand-green-0)] tw:dark:[--checkbox-checked-bg:theme(colors.brand-green-1)] tw:[--checkbox-checked-border:theme(colors.brand-green-0/90%)]',
};

type Color = keyof typeof colors;

export function Checkbox({
  color = 'dark/zinc',
  className,
  ...props
}: {
  color?: Color;
  className?: string;
} & Omit<Headless.CheckboxProps, 'as' | 'className'>) {
  return (
    <Headless.Checkbox
      data-slot="control"
      {...props}
      className={clsx(className, 'tw:group tw:inline-flex tw:focus:outline-none')}
    >
      <span className={clsx([base, colors[color]])}>
        <svg
          className="tw:size-4 tw:stroke-[var(--checkbox-check)] tw:opacity-0 tw:group-data-[checked]:opacity-100 tw:sm:h-3.5 tw:sm:w-3.5"
          viewBox="0 0 14 14"
          fill="none"
        >
          {/* Checkmark icon */}
          <path
            className="tw:opacity-100 tw:group-data-[indeterminate]:opacity-0"
            d="M3 8L6 11L11 3.5"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {/* Indeterminate icon */}
          <path
            className="tw:opacity-0 tw:group-data-[indeterminate]:opacity-100"
            d="M3 7H11"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </Headless.Checkbox>
  );
}
